// Call Analytics tab: call stats, filters, table, call details modal
import React, { useState, useEffect, useMemo, useCallback, ChangeEvent } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { X } from 'lucide-react';
import { getWorkflowTranscript, deleteCalls } from 'services/api/WorkflowServiceAPI';
import {
    formatDisposition,
    formatLeadStatus,
    formatCallOutcome,
    formatDuration,
    setDateTimes,
    formatDurationTooltip,
    downloadCsv,
    getDateRangeForRangeType,
} from '../utils';
import { Statistic, BulkActions, CallDetailsModal, CallsTable } from '../components';
import { Button, Input, Pagination, Select } from 'common/components';
import { useGetAgenticWorkflowDetails, useGetCallsData } from 'services/hooks/queries';
import { SortDirection, SortField } from 'types/call.type';
import type {
    Lead,
    DateRangeType,
    HandleDeleteCallsParams,
    HandleExportCSVParams,
} from '../AgenticWorkflowDetail.types';
import styles from '../AgenticWorkflowDetail.module.scss';

export type AgenticWorkflowOutletContext = {
    initialStartDate: Date;
    initialEndDate: Date;
    refetchWorkflowDetails: () => Promise<unknown>;
    openNotification: (state: {
        isOpen: boolean;
        title: string;
        message: string;
        type: 'success' | 'error';
        body: React.ReactNode;
        buttons: { label: string; onClick: () => void; style?: React.CSSProperties }[];
    }) => void;
    closeNotification: () => void;
};

const CallAnalyticsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { refetchWorkflowDetails, openNotification, closeNotification, initialStartDate, initialEndDate } =
        useOutletContext<AgenticWorkflowOutletContext>();

    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([initialStartDate, initialEndDate]);
    const [dateRangeType, setDateRangeType] = useState<DateRangeType>('today');
    const [filterLeadStatus, setFilterLeadStatus] = useState('');
    const [filterCallStatus, setFilterCallStatus] = useState('');
    const [filterCallDirection, setFilterCallDirection] = useState('');
    const [filterDisposition, setFilterDisposition] = useState('');
    const [sortField, setSortField] = useState<SortField>('callDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedCall, setSelectedCall] = useState<Lead | null>(null);
    const [showCallDetailsModal, setShowCallDetailsModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const debouncedSetSearchTerm = useCallback(
        debounce((value: string) => setSearchTerm(value), 300),
        []
    );

    useEffect(() => () => debouncedSetSearchTerm.cancel(), [debouncedSetSearchTerm]);

    const { startWithTime, endWithTime } = setDateTimes(dateRange);

    const queryParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        callStatus: filterCallStatus,
        partyStage: filterLeadStatus,
        callDirection: filterCallDirection,
        disposition: filterDisposition,
        search: searchTerm,
        perPage: rowsPerPage,
        sortOrder: sortDirection,
        sortBy: sortField,
        currentPage,
        combinedOverdue: dateRangeType === 'today',
    };

    const statisticQueryParams = {
        id: id || '',
        startDate: startWithTime,
        endDate: endWithTime,
        callStatus: filterCallStatus,
        partyStage: filterLeadStatus,
        callDirection: filterCallDirection,
        disposition: filterDisposition,
        search: searchTerm,
        currentPage,
        perPage: rowsPerPage,
        isRefetchEnabled: true,
    };

    const { agenticWorkflowDetails: workflowDetails } = useGetAgenticWorkflowDetails(statisticQueryParams);
    const { callsData } = useGetCallsData(queryParams, true, workflowDetails?.status === 'ACTIVE');

    const leads = useMemo<Lead[]>(() => {
        if (!callsData?.data?.length) return [];
        return callsData.data.map((call) => {
            const callDateTime = new Date(call.startTime || call.scheduledDate);
            const durationSeconds = call.callDuration || 0;
            return {
                id: call.crmId,
                callId: call.callId,
                callDate: callDateTime.toLocaleDateString('en-US'),
                callTime: callDateTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
                outcome: call.status || 'PENDING',
                duration: formatDuration(durationSeconds),
                durationTooltip: formatDurationTooltip(durationSeconds),
                crmStatus: call.partyStage || 'NEW_LEAD',
                crmId: call.crmId,
                name: call.contactName || 'Unknown',
                number: call.phoneNumber || 'Unknown',
                transferTo: call.transferTo || '',
                disposition: call.disposition || '',
                callDirection: call.callDirection,
                action: '',
                recording: call.callId,
                transcript: call.callId,
            };
        });
    }, [callsData]);

    const totalPages = callsData?.pagination?.totalPages || 1;
    const totalRecords = callsData?.pagination?.totalRecords || 0;
    const currentPageFromAPI = callsData?.pagination?.currentPage || currentPage;
    const perPageFromAPI = callsData?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        const paginationData = callsData?.pagination;
        if (paginationData?.currentPage) setCurrentPage(paginationData.currentPage);
        if (paginationData?.perPage) setRowsPerPage(paginationData.perPage);
    }, [callsData?.pagination]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) setCurrentPage(totalPages);
    }, [totalPages, currentPage]);

    const handleSortChange = (field: SortField, newSortDirection: SortDirection) => {
        setSortField(field);
        setSortDirection(newSortDirection);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setSearchTerm('');
        setFilterLeadStatus('');
        setFilterCallStatus('');
        setFilterCallDirection('');
        setFilterDisposition('');
        setDateRangeType('today');
        setDateRange([new Date(), new Date()]);
        setCurrentPage(1);
        setSelectedCalls([]);
    };

    const handleCheckboxChange = (callId: string) => {
        setSelectedCalls((prev) => (prev.includes(callId) ? prev.filter((id) => id !== callId) : [...prev, callId]));
    };

    const handleSelectAllCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelectedCalls(leads.map((l) => l.callId));
        } else {
            const pageIds = leads.map((l) => l.callId);
            setSelectedCalls(selectedCalls.filter((id) => !pageIds.includes(id)));
        }
    };

    const handleDeleteCalls = async ({ selectedCallId }: HandleDeleteCallsParams) => {
        if (!selectedCallId && !selectedCalls.length) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one call to delete.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        openNotification({
            isOpen: true,
            title: 'Confirm Deletion',
            message: `Are you sure you want to delete ${selectedCallId ? 'this call' : `${selectedCalls.length} call(s)`}?`,
            type: 'error',
            body: null,
            buttons: [
                { label: 'Cancel', onClick: closeNotification, style: { backgroundColor: '#9b9b9b' } },
                {
                    label: 'Delete',
                    onClick: async () => {
                        closeNotification();
                        try {
                            setIsDeleting(true);
                            const idsToDelete = selectedCallId ? [selectedCallId] : selectedCalls;
                            await deleteCalls(idsToDelete);
                            openNotification({
                                isOpen: true,
                                title: 'Deletion Successful',
                                message: 'Calls deleted successfully',
                                type: 'success',
                                body: null,
                                buttons: [],
                            });
                            await refetchWorkflowDetails();
                            setSelectedCalls([]);
                        } catch (err) {
                            console.error('Error deleting calls:', err);
                            openNotification({
                                isOpen: true,
                                title: 'Error',
                                message: `Failed to delete calls: ${(err as Error).message || 'Unknown error'}`,
                                type: 'error',
                                body: null,
                                buttons: [],
                            });
                        } finally {
                            setIsDeleting(false);
                        }
                    },
                    style: { backgroundColor: '#f44336' },
                },
            ],
        });
    };

    const handleDateRangeChange = (range: string) => {
        const newRange = getDateRangeForRangeType(range);
        setDateRangeType(range as DateRangeType);
        if (newRange) {
            setCurrentPage(1);
            setDateRange(newRange);
        }
    };

    const handleExportCSV = async ({ selectedCallId }: HandleExportCSVParams) => {
        if (!selectedCalls.length && !selectedCallId) {
            openNotification({
                isOpen: true,
                title: 'Selection Required',
                message: 'Please select at least one call to export.',
                type: 'error',
                body: null,
                buttons: [],
            });
            return;
        }
        try {
            openNotification({
                isOpen: true,
                title: 'Processing',
                message: 'Preparing CSV export including transcripts...',
                type: 'success',
                body: null,
                buttons: [],
            });
            const selectedLeads = leads.filter((lead) =>
                selectedCallId ? lead.callId === selectedCallId : selectedCalls.includes(lead.callId)
            );
            const headers = [
                'Call ID',
                'Call Date',
                'Call Time',
                'Call Status',
                'Duration',
                'Party Status',
                'Contact ID',
                'Name',
                'Number',
                'Transfer To',
                'Disposition',
                'Transcript',
            ];
            const csvRows: string[][] = [];
            for (const lead of selectedLeads) {
                let transcript = '';
                if (lead.transcript) {
                    try {
                        const transcriptData = await getWorkflowTranscript(lead.callId);
                        if (transcriptData?.speeches && Array.isArray(transcriptData.speeches)) {
                            transcript = transcriptData.speeches
                                .map(
                                    (e: { speaker: string; text: string }) =>
                                        `${e.speaker === 'assistant' ? 'SLAI Agent' : lead.name || 'Customer'}: ${e.text}`
                                )
                                .join('\n');
                        } else if (transcriptData?.transcript) {
                            transcript = transcriptData.transcript;
                        }
                    } catch {
                        // ignore
                    }
                }
                csvRows.push([
                    lead.callId,
                    lead.callDate,
                    lead.callTime,
                    formatCallOutcome(lead.outcome),
                    lead.duration,
                    formatLeadStatus(lead.crmStatus),
                    lead.id,
                    lead.name,
                    lead.number,
                    lead.transferTo,
                    formatDisposition(lead.disposition),
                    transcript,
                ]);
            }
            const dateString = new Date().toISOString().split('T')[0];
            downloadCsv(headers, csvRows, `workflow_${dateString}.csv`);
            openNotification({
                isOpen: true,
                title: 'Export Complete',
                message: `Successfully exported ${selectedLeads.length} call(s) to CSV.`,
                type: 'success',
                body: null,
                buttons: [],
            });
            setSelectedCalls([]);
        } catch (err) {
            console.error('Error exporting CSV:', err);
            openNotification({
                isOpen: true,
                title: 'Export Failed',
                message: 'Failed to export CSV. Please try again.',
                type: 'error',
                body: null,
                buttons: [],
            });
        }
    };

    const handleCallDetails = (lead: Lead) => {
        setSelectedCall(lead);
        setShowCallDetailsModal(true);
    };

    const onCloseCallDetailsModal = () => {
        setShowCallDetailsModal(false);
        setSelectedCall(null);
    };

    const isWorkflowLoading = !workflowDetails;

    return (
        <>
            <Statistic callStats={workflowDetails?.callStatistic} />

            <div className={styles.workflowLeadsContainer}>
                <div className={styles.filterSection}>
                    <div className={styles.searchBox}>
                        <Input
                            type='search'
                            placeholder='Search by name, number or ID'
                            onChange={(e) => {
                                if (e.target.value.length > 1 || e.target.value.length === 0) {
                                    debouncedSetSearchTerm(e.target.value);
                                }
                            }}
                        />
                    </div>
                    <div className={styles.callDirectionFilter}>
                        <Select
                            value={filterCallDirection}
                            onChange={(e) => {
                                setFilterCallDirection(e.target.value);
                                setCurrentPage(1);
                            }}
                            options={[
                                { value: '', label: 'All Call Directions' },
                                { value: 'OUTBOUND', label: 'Outbound Calls' },
                                { value: 'INBOUND', label: 'Inbound Calls' },
                            ]}
                        />
                    </div>
                    <Button
                        variant='outline-primary'
                        icon={X}
                        onClick={handleClearFilter}
                        size='sm'
                        className={styles.clearFilterBtn}
                    >
                        Clear Filters
                    </Button>
                </div>
                <div className={styles.workflowLeadsSection}>
                    <div className={styles.leadsTableHover}>
                        <div className={styles.leadsTableContainer}>
                            <CallsTable
                                isLoading={isWorkflowLoading}
                                leads={leads}
                                selectedCalls={selectedCalls}
                                selectedCall={selectedCall}
                                sortField={sortField}
                                sortDirection={sortDirection}
                                searchTerm={searchTerm}
                                filterLeadStatus={filterLeadStatus}
                                filterCallStatus={filterCallStatus}
                                filterCallDirection={filterCallDirection}
                                filterDisposition={filterDisposition}
                                partyStages={workflowDetails?.partyStages ?? []}
                                onSortChange={handleSortChange}
                                onFilterChange={(filter, value) => {
                                    if (filter === 'leadStatus') setFilterLeadStatus(value);
                                    else if (filter === 'callStatus') setFilterCallStatus(value);
                                    else if (filter === 'callDirection') setFilterCallDirection(value);
                                    else setFilterDisposition(value);
                                    setCurrentPage(1);
                                }}
                                onSelectAllCheckboxChange={handleSelectAllCheckboxChange}
                                onCheckboxChange={handleCheckboxChange}
                                onCallDetails={handleCallDetails}
                                dateRangeConfig={{
                                    type: dateRangeType,
                                    onTypeChange: handleDateRangeChange,
                                    setDateRange,
                                }}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <BulkActions
                            selectedCalls={selectedCalls}
                            handleDeleteCalls={handleDeleteCalls}
                            handleExportCSV={handleExportCSV}
                        />
                        <Pagination
                            currentPage={currentPageFromAPI}
                            totalPages={totalPages}
                            totalRecords={totalRecords}
                            rowsPerPage={perPageFromAPI}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className={styles.agenticPagination}
                        />
                    </div>
                </div>
            </div>
            <CallDetailsModal
                isOpen={showCallDetailsModal}
                selectedCall={selectedCall}
                setSelectedCall={setSelectedCall}
                leads={leads}
                onClose={onCloseCallDetailsModal}
                handleDeleteCall={handleDeleteCalls}
                handleExportCSV={handleExportCSV}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalPages={totalPages}
                isLoading={isWorkflowLoading}
            />
        </>
    );
};

export default CallAnalyticsPage;
